import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  LayoutDashboard, 
  FileText, 
  FolderKanban, 
  MessageSquare, 
  LogOut,
  Menu,
  X
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerSidebar = ({ customer }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
    };
    
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    if (isMobile && isOpen) {
      // Use a small delay to avoid cascading renders
      const timer = setTimeout(() => setIsOpen(false), 0);
      return () => clearTimeout(timer);
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle swipe to close
  useEffect(() => {
    if (!isMobile || !isOpen) return;

    let startX = 0;
    const handleTouchStart = (e) => {
      startX = e.touches[0].clientX;
    };
    const handleTouchEnd = (e) => {
      const endX = e.changedTouches[0].clientX;
      if (startX - endX > 50) {
        setIsOpen(false);
      }
    };

    document.addEventListener("touchstart", handleTouchStart);
    document.addEventListener("touchend", handleTouchEnd);
    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isMobile, isOpen]);

  const menuItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/client/dashboard" },
    { icon: FileText, label: "Invoices", path: "/client/invoices" },
    { icon: FolderKanban, label: "Projects", path: "/client/projects" },
    { icon: FileText, label: "Documents", path: "/client/documents" },
    { icon: MessageSquare, label: "Messages", path: "/client/messages" },
  ];

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/customer/logout`, {
        method: "POST",
        credentials: "include"
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    navigate("/client/login", { replace: true });
  };

  const handleNavClick = (path) => {
    navigate(path);
    if (isMobile) {
      setIsOpen(false);
    }
  };

  return (
    <>
      {/* Mobile hamburger button */}
      {isMobile && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed top-4 left-4 z-50 p-2 bg-[#121214] border border-[#27272A] rounded-lg text-[#FAFAFA] hover:border-[#3B82F6] transition-colors"
          data-testid="mobile-menu-btn"
          aria-label="Open menu"
        >
          <Menu size={24} />
        </button>
      )}

      {/* Overlay */}
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 backdrop-blur-sm"
          onClick={() => setIsOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-full w-64 bg-[#121214] border-r border-[#27272A] flex flex-col z-50 transition-transform duration-300 ease-in-out ${
          isMobile && !isOpen ? "-translate-x-full" : "translate-x-0"
        }`}
        data-testid="customer-sidebar"
      >
        {/* Header with close button on mobile */}
        <div className="px-3 py-3 flex items-center justify-between">
          <div>
            <img
              src="/logo-reflection-trimmed.png"
              alt="DopeInHD"
              className="h-10 md:h-11 lg:h-12 w-auto max-w-full object-contain block"
            />
            <p className="text-xs text-[#71717A] mt-2">Client Portal</p>
          </div>
          {isMobile && (
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 text-[#A1A1AA] hover:text-[#FAFAFA] transition-colors"
              data-testid="close-sidebar-btn"
              aria-label="Close menu"
            >
              <X size={24} />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <button
                key={item.path}
                onClick={() => handleNavClick(item.path)}
                className={`sidebar-item w-full flex items-center gap-3 px-6 py-3 text-left transition-all ${
                  isActive 
                    ? "active text-[#D4AF37]" 
                    : "text-[#A1A1AA] hover:text-[#FAFAFA]"
                }`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <Icon size={20} />
                <span className="font-medium">{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* User Profile */}
        <div className="p-4 border-t border-[#27272A]">
          <div className="flex items-center gap-3 mb-3">
            {customer?.picture ? (
              <img 
                src={customer.picture} 
                alt={customer.name} 
                className="w-10 h-10 rounded-full border-2 border-[#3B82F6]"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-[#3B82F6] flex items-center justify-center text-white font-bold">
                {customer?.name?.charAt(0) || "C"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#FAFAFA] truncate">{customer?.name}</p>
              <span className="text-xs text-[#3B82F6]">Client</span>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm text-[#A1A1AA] hover:text-[#EF4444] hover:bg-[#EF4444]/10 rounded-lg transition-all"
            data-testid="customer-logout-btn"
          >
            <LogOut size={16} />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default CustomerSidebar;
